import React, { useState } from "react";
import { FaChevronDown, FaFilter, FaSearch, FaTimes } from "react-icons/fa";

function PalmaresToolbar({
  filters,
  years = [],
  onFilterChange,
  onReset,
  activeFiltersCount = 0,
  resultsCount = 0,
  totalCount = 0,
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="sticky top-[88px] z-30 rounded-[2rem] border border-slate-200 bg-white/95 p-4 shadow-xl shadow-slate-200/60 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/20 lg:top-[104px]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-bold text-slate-950 dark:text-white">
            {resultsCount} résultat{resultsCount > 1 ? "s" : ""} affiché
            {resultsCount > 1 ? "s" : ""} / {totalCount}
          </p>

          <p className="mt-1 text-xs font-medium text-slate-500 dark:text-white/45">
            Filtre ton palmarès par podium, année ou document.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5 dark:bg-white dark:text-slate-950"
        >
          <FaFilter />
          Filtres
          {activeFiltersCount > 0 && (
            <span className="rounded-full bg-orange-500 px-2 py-0.5 text-xs text-white">
              {activeFiltersCount}
            </span>
          )}
          <FaChevronDown className={`transition ${isOpen ? "rotate-180" : ""}`} />
        </button>
      </div>

      <div className="mt-4">
        <label className="relative block">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="search"
            value={filters.search}
            onChange={(event) => onFilterChange("search", event.target.value)}
            placeholder="Rechercher une compétition, une observation, un résultat..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-bold text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-amber-300 focus:bg-white focus:ring-4 focus:ring-amber-100 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:focus:border-amber-300/50 dark:focus:bg-slate-950 dark:focus:ring-amber-500/10"
          />
        </label>
      </div>

      {isOpen && (
        <div className="mt-4 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.03]">
          <div className="grid gap-4 md:grid-cols-4">
            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Classement
              </span>

              <select
                value={filters.rank}
                onChange={(event) => onFilterChange("rank", event.target.value)}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-amber-300 focus:ring-4 focus:ring-amber-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-amber-300/50 dark:focus:ring-amber-500/10"
              >
                <option value="all">Tous</option>
                <option value="first">1ère place</option>
                <option value="second">2ème place</option>
                <option value="third">3ème place</option>
                <option value="other">Autres places</option>
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Année
              </span>

              <select
                value={filters.year}
                onChange={(event) => onFilterChange("year", event.target.value)}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-amber-300 focus:ring-4 focus:ring-amber-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-amber-300/50 dark:focus:ring-amber-500/10"
              >
                <option value="all">Toutes les années</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Documents
              </span>

              <select
                value={filters.document}
                onChange={(event) =>
                  onFilterChange("document", event.target.value)
                }
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-amber-300 focus:ring-4 focus:ring-amber-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-amber-300/50 dark:focus:ring-amber-500/10"
              >
                <option value="all">Tous</option>
                <option value="with">Avec document</option>
                <option value="without">Sans document</option>
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                Tri
              </span>

              <select
                value={filters.sort}
                onChange={(event) => onFilterChange("sort", event.target.value)}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-amber-300 focus:ring-4 focus:ring-amber-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:border-amber-300/50 dark:focus:ring-amber-500/10"
              >
                <option value="rank">Podiums d'abord</option>
                <option value="dateDesc">Plus récent</option>
                <option value="dateAsc">Plus ancien</option>
                <option value="titleAsc">Nom A-Z</option>
              </select>
            </label>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onReset}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-red-200 hover:text-red-600 dark:border-white/10 dark:bg-slate-950 dark:text-white"
            >
              <FaTimes />
              Réinitialiser
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PalmaresToolbar;